import { DragIndicator } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ReactNode } from "react";

import SectionHeader from "src-next/shared/ui/sections/SectionHeader";

import type { CannabinoidSectionId } from "./useCannabinoidSectionsLayout";

interface CannabinoidSortableSectionProps {
  id: CannabinoidSectionId;
  title: string;
  subtitle?: string;
  icon: ReactNode;
  badge?: ReactNode;
  headerActions?: ReactNode;
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
  children: ReactNode;
}

/** Seção arrastável e colapsável — mesmo padrão do FluxZap / PatientDash. */
export function CannabinoidSortableSection({
  id,
  title,
  subtitle,
  icon,
  badge,
  headerActions,
  isCollapsed,
  onToggleCollapsed,
  children,
}: CannabinoidSortableSectionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <Box
      ref={setNodeRef}
      id={`cannabinoid-section-${id}`}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.45 : 1,
        zIndex: isDragging ? 999 : "auto",
      }}
    >
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: isDragging ? "primary800.main" : "divider",
          bgcolor: "neutral0.main",
          overflow: "visible",
          transition: "border-color 0.15s ease",
        }}
      >
        <CardContent
          sx={{ py: 2, px: { xs: 2, md: 2.5 }, "&:last-child": { pb: 2 } }}
        >
          <SectionHeader
            icon={icon}
            title={title}
            subtitle={subtitle}
            badge={badge}
            isCollapsed={isCollapsed}
            onCollapsedChange={onToggleCollapsed}
            actions={
              <>
                {headerActions}
                <Tooltip title="Arrastar para reordenar" arrow>
                  <IconButton
                    size="small"
                    aria-label="Arrastar para reordenar a seção"
                    {...attributes}
                    {...listeners}
                    sx={{
                      cursor: isDragging ? "grabbing" : "grab",
                      color: "text.disabled",
                      "&:hover": { color: "text.secondary" },
                      touchAction: "none",
                    }}
                  >
                    <DragIndicator fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            }
          >
            {children}
          </SectionHeader>
        </CardContent>
      </Card>
    </Box>
  );
}

/** Chip de progresso dos documentos. */
export function DocumentsProgressBadge({
  uploadedCount,
  totalCount,
}: {
  uploadedCount: number;
  totalCount: number;
}) {
  const complete = uploadedCount === totalCount && totalCount > 0;
  return (
    <Chip
      label={`${uploadedCount}/${totalCount} enviados`}
      size="small"
      color={complete ? "success" : "default"}
      sx={{ fontWeight: 700, height: 24, fontSize: "0.8125rem" }}
    />
  );
}
